import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "ScreenTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Wedge Wire Screen",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Precision Stainless Steel Screen Tubes",
      lead: "Engineered cylindrical wedge wire screens and all-welded tube assemblies designed for severe differential pressure, high collapse resistance, and precision solids retention across industrial filtration systems.",
      secondaryCta: {
        label: "Wedge Wire Overview",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Stainless steel wire mesh screen tube cylinder",
      },
      cardNote: "All-Welded Cylindrical Screens",
      cardLink: {
        label: "Technical Specs",
        to: "/ScreenTubesPage#specifications",
      },
      specs: [
        { label: "Slot Aperture", value: "0.02 – 5.0 mm" },
        { label: "Cylinder OD", value: "10 – 600 mm" },
        { label: "Standard Alloys", value: "AISI 304 / 316L" },
        { label: "Element Length", value: "Up to 3,000 mm" },
      ],
    },
    overview: {
      eyebrow: "Structural Rigidity & Flow Efficiency",
      heading: "Continuous Slot Geometry for Demanding Tubular Filtration",
      paragraphs: [
        "Screen tubes manufactured from resistance-welded stainless steel wedge wire provide high structural rigidity, continuous uniform slot openings, and exceptional resistance to collapse under high external or internal operating pressures.",
        "The inward-widening V-profile wire geometry creates a two-point particle contact at the outer screen face. Solid particles smaller than the slot width pass freely through without wedging or blinding, while larger particulates are retained on the outer boundary, making the elements easily cleanable via backwashing or ultrasonic bathing.",
        "Fabricated in diameters ranging from 10 mm to 600 mm and single-piece lengths up to 3,000 mm, our screen tubes are fitted with custom weld rings, flanged ends, threaded connectors, or internal perforated reinforcement cores tailored to specific process vessel geometry.",
      ],
      keyPoints: [
        "Continuous V-wire configuration delivers smooth internal or external filtering surfaces with minimal flow resistance and high open area.",
        "Resistance-welded joints at every profile wire and support rod intersection guarantee high burst and collapse resistance under pressure surges.",
        "Precision manufacturing in AISI 304 and AISI 316L stainless steel allows repeated chemical flushing, steam sterilization, and backwash regeneration.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Stainless steel wire mesh screen tube cylinder",
      },
    },
    specTable: {
      heading: "Screen Tube Dimensional & Manufacturing Parameters",
      intro: "Standard dimensional ranges for cylindrical wedge wire screen tubes. Custom slot widths, reinforcement rods, and fitting styles are manufactured to customer drawings.",
      columns: [
        { key: "parameter", label: "Parameter", align: "left" },
        { key: "range", label: "Standard Range", align: "left" },
        { key: "tolerance", label: "Manufacturing Tolerance", align: "left" },
        { key: "notes", label: "Engineering Notes", align: "left" },
      ],
      rows: [
        {
          id: "slot-width",
          parameter: "Slot Opening Range",
          range: "0.02 – 5.0 mm",
          tolerance: "±0.015 mm",
          notes: "Continuous slot spacing across entire working length",
        },
        {
          id: "diameter",
          parameter: "Outside Diameter (OD)",
          range: "10 – 600 mm",
          tolerance: "±0.5 mm",
          notes: "Custom mandrel sizing available for tight vessel housings",
        },
        {
          id: "length",
          parameter: "Element Overall Length",
          range: "100 – 3,000 mm",
          tolerance: "±1.0 mm",
          notes: "Segmented assemblies joined via precision circumferential welding",
        },
        {
          id: "materials",
          parameter: "Alloy Grades",
          range: "AISI 304, AISI 316L",
          tolerance: "Standard chemistry",
          notes: "Material test reports available upon order specification",
        },
        {
          id: "end-connections",
          parameter: "End Fittings & Terminations",
          range: "Flanges, NPT/BSPT Threads, Weld Rings",
          tolerance: "Machined to standard",
          notes: "Plain cut ends or fully machined stainless collars",
        },
      ],
      caption: "Values represent verified engineering ranges. Additional configurations engineered to specification.",
    },
    features: {
      heading: "Design Advantages & Construction Standards",
      intro: "Engineered specifically to solve wire migration, blinding, and structural fatigue in high-load continuous separation.",
      items: [
        {
          title: "Non-Clogging V-Wire Profile",
          text: "Continuous narrow surface slot opening expands inward, ensuring that trapped solids break away cleanly during reverse pulse cleaning.",
        },
        {
          title: "All-Welded Structural Integrity",
          text: "Every intersection of surface profile wire and longitudinal support rod is resistance-welded, creating a monolithic, rigid cage.",
        },
        {
          title: "Customized Flow Orientation",
          text: "Available in both flow-out-to-in (FOTI) and flow-in-to-out (FITO) configurations to match external cake filtration or internal retention.",
        },
        {
          title: "Precision End Fabrication",
          text: "Equipped with machined solid end rings, sanitary flanges, threaded nipples, or closed end caps welded flush with the screen tube.",
        },
      ],
    },
    related: {
      heading: "Specialized Screen Tube Configurations",
      intro: "Explore dedicated screen tube variants engineered for deep well intakes, underdrains, and high-pressure candle vessel filters.",
      cards: [
        {
          title: "Screen Pipes & Lateral Hubs",
          text: "Continuous V-profile wire welded to longitudinal support rods for deep intake wells, media underdrains, and uniform fluid distribution.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Stainless steel wedge wire screen pipe with continuous slot and support rods",
          },
          badge: "Intake & Underdrain",
          linkLabel: "View Screen Pipes",
        },
        {
          title: "Wedge Wire Tubes & Candle Elements",
          text: "All-welded continuous-slot screen tubes and tubular candle elements engineered for high-pressure separation, catalyst retention, and severe solid-liquid straining.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Industrial stainless steel wedge wire candle filter element for pressurized vessels",
          },
          badge: "Pressure Vessels",
          linkLabel: "View Wedge Wire Tubes",
        },
        {
          title: "Wedge Wire Screen Parent Hub",
          text: "Comprehensive overview of wedge wire flat panels, curved sieve bends, cylindrical screens, and specialized industrial nozzles.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Wedge wire screens product overview",
          },
          badge: "Parent Category",
          linkLabel: "Back to Wedge Wire",
        },
      ],
    },
    cta: {
      id: "screen-tubes-quote",
      eyebrow: "Custom Cylindrical Screen Manufacturing",
      heading: "Request a Technical Quote for Screen Tubes",
      lead: "Specify your required slot opening, outer diameter, overall length, flow direction, and end fitting configuration. Our engineering team prepares detailed dimensional and specification proposals.",
      secondaryLabel: "Explore Wedge Wire Screens",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "ScreenTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "楔形丝筛网",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "高精度不锈钢圆柱筛管与滤筒",
      lead: "采用定制加工不锈钢楔形截面圆筒结构，专为工业过滤系统中严苛的高压差、高抗压溃要求及精确固液分离工况而设计。",
      secondaryCta: {
        label: "楔形丝总览",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "不锈钢编织及烧结丝网精密滤筒筛管",
      },
      cardNote: "全焊接圆柱滤筒组件",
      cardLink: {
        label: "技术规格",
        to: "/ScreenTubesPage#specifications",
      },
      specs: [
        { label: "缝隙精度", value: "0.02 – 5.0 mm" },
        { label: "圆筒外径", value: "10 – 600 mm" },
        { label: "标准材质", value: "AISI 304 / 316L" },
        { label: "单支长度", value: "最高 3,000 mm" },
      ],
    },
    overview: {
      eyebrow: "结构刚性与流体通量",
      heading: "连续微米级缝隙结构，满足严苛筒状过滤需求",
      paragraphs: [
        "互拓生产的圆柱形楔形丝筛管采用数控全电阻熔焊工艺制造，具有出色的结构刚性、均匀的连续缝隙几何形状以及在内外压差下的高抗压溃能力。",
        "外小内大的 V 型截面丝几何结构在外表面形成两点线状颗粒接触。小于缝隙宽度的微粒在流体带动下顺畅通过扩散通道，杜绝孔道卡塞；截留的大颗粒聚集在筛网外表面，通过反冲洗或超声波清洗即可迅速恢复初始压降。",
        "筛管外径涵盖 10 mm 至 600 mm，单段长度最高可达 3,000 mm。两端可根据工况配备机械加工法兰、外螺纹接头、平焊端环或内置多孔冲孔加强骨架，无缝接入各类压力容器与管路系统。",
      ],
      keyPoints: [
        "连续 V 型丝构造，表面光滑平整无毛刺，流阻极小且具备高有效开孔率。",
        "全电阻熔焊点连接每一根型材丝与纵向支撑筋条，耐受流体冲击与高压脉冲。",
        "选用优质 AISI 304 及 316L 不锈钢材料，耐受酸碱化学浸洗、蒸汽高温灭菌及周期性反冲洗。",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "不锈钢编织及烧结丝网精密滤筒筛管",
      },
    },
    specTable: {
      heading: "筛管尺寸与机械制造参数",
      intro: "圆柱形楔形丝筛管标准加工范围。缝隙尺寸、加强筋排布及端部接头样式均可按客户图纸定制生产。",
      columns: [
        { key: "parameter", label: "参数项目", align: "left" },
        { key: "range", label: "标准制造范围", align: "left" },
        { key: "tolerance", label: "制造公差", align: "left" },
        { key: "notes", label: "工程说明", align: "left" },
      ],
      rows: [
        {
          id: "slot-width",
          parameter: "缝隙宽度范围",
          range: "0.02 – 5.0 mm",
          tolerance: "±0.015 mm",
          notes: "沿整个有效工作长度保持缝隙均一",
        },
        {
          id: "diameter",
          parameter: "圆筒外径 (OD)",
          range: "10 – 600 mm",
          tolerance: "±0.5 mm",
          notes: "可定制专用心轴尺寸以适配紧凑容器内壁",
        },
        {
          id: "length",
          parameter: "单段总长度",
          range: "100 – 3,000 mm",
          tolerance: "±1.0 mm",
          notes: "长尺寸可通过环缝精密对接焊接成型",
        },
        {
          id: "materials",
          parameter: "不锈钢牌号",
          range: "AISI 304, AISI 316L",
          tolerance: "标准成分",
          notes: "按订单要求可提供材质检验报告与成分证明",
        },
        {
          id: "end-connections",
          parameter: "端部接口形式",
          range: "平焊法兰、管螺纹 (NPT/BSPT)、平端环",
          tolerance: "符合标准接口尺寸",
          notes: "可提供切平端头或一体式机加工套环",
        },
      ],
      caption: "所列数据为确认的制造能力范围。如有特殊非标要求，支持按图定制加工。",
    },
    features: {
      heading: "结构特点与制造优势",
      intro: "专为解决高负荷连续固液分离中出现的颗粒卡堵、丝材位移及疲劳损坏难题而设计。",
      items: [
        {
          title: "防堵塞 V 型丝几何设计",
          text: "外表面狭窄缝隙向内部逐步加宽，确保反冲洗时积聚的固体滤饼迅速脱离筛面。",
        },
        {
          title: "全焊式高刚性笼状骨架",
          text: "表面轮廓丝与内部纵向支撑筋条每个交叉点均经过全熔透电阻焊接，形成坚固一体的圆柱笼体。",
        },
        {
          title: "灵活的介质流向配置",
          text: "支持外进内出 (FOTI) 或内进外出 (FITO) 两种流向结构，分别适配表面滤饼截留与筒内介质拦截。",
        },
        {
          title: "精密端部密封与机械加工",
          text: "配置机加工实心端环、卫生级快装法兰或螺纹接头，焊接平滑光洁，彻底杜绝侧漏。",
        },
      ],
    },
    related: {
      heading: "专用筛管与管状构件系列",
      intro: "了解适用于深井滤水、集水配水管路及承压烛式过滤器的专用筛管分支产品。",
      cards: [
        {
          title: "滤水筛管与集配水管",
          text: "连续 V 型截面丝全焊于纵向支撑筋条结构，为深水取水井、树脂截留及压力容器提供可靠介质截留与均匀流体分布。",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "不锈钢锲形丝连续缝隙滤水筛管与支撑筋条结构",
          },
          badge: "深井与集配水",
          linkLabel: "查看滤水筛管",
        },
        {
          title: "楔形丝烛式滤芯与筛管",
          text: "全焊式连续精密缝隙楔形丝滤芯与筛管，专为高压固液分离、催化剂回收及严苛工艺介质截留而设计。",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "用于承压容器的不锈钢全焊式楔形丝烛式滤芯与过滤管",
          },
          badge: "承压容器滤元",
          linkLabel: "查看烛式滤芯",
        },
        {
          title: "楔形丝筛网产品中心",
          text: "包含平面筛板、弧形筛面、圆柱筛筒以及工业滤帽等全系列全焊式楔形丝过滤产品总览。",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "楔形丝筛网产品总览",
          },
          badge: "所属分类总览",
          linkLabel: "返回楔形丝总览",
        },
      ],
    },
    cta: {
      id: "screen-tubes-quote",
      eyebrow: "圆柱形筛管非标定制",
      heading: "获取精密筛管与滤筒技术方案及报价",
      lead: "请提供所需的缝隙宽度、圆筒外径、单支长度、介质流向及端部接口形式。我们的工程技术团队将为您出具详细图纸与技术报价。",
      secondaryLabel: "返回楔形丝产品分类",
      secondaryTo: "/WedgeWireScreenPage",
      note: "按订单要求可提供材质检验报告 (MTR) 与 PMI 合金成分验证报告。",
    },
  },
  es: {
    breadcrumbKey: "ScreenTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Alambre en Cuña",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubos de Filtración de Precisión en Acero Inoxidable",
      lead: "Cilindros de malla y perfiles de cuña totalmente soldados, diseñados para resistir presiones diferenciales severas y retener sólidos con alta precisión en sistemas de filtración industrial.",
      secondaryCta: {
        label: "Gama Alambre en Cuña",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Cilindro de tubo filtrante de malla de alambre de acero inoxidable",
      },
      cardNote: "Tubos Filtrantes Soldados",
      cardLink: {
        label: "Datos Técnicos",
        to: "/ScreenTubesPage#specifications",
      },
      specs: [
        { label: "Abertura de ranura", value: "0,02 – 5,0 mm" },
        { label: "Diámetro exterior", value: "10 – 600 mm" },
        { label: "Aleaciones estándar", value: "AISI 304 / 316L" },
        { label: "Longitud elemento", value: "Hasta 3.000 mm" },
      ],
    },
    overview: {
      eyebrow: "Rigidez Estructural y Capacidad de Flujo",
      heading: "Geometría de Ranura Continua para Filtración Tubular Exigente",
      paragraphs: [
        "Los tubos de filtración en alambre de cuña soldados por resistencia ofrecen una rigidez estructural sobresaliente y una ranura uniforme que resiste elevadas cargas de colapso y sobrepresiones operativas.",
        "El perfil en V con ensanchamiento hacia el interior crea un contacto de dos puntos con las partículas en la cara exterior. Los sólidos más pequeños pasan libremente sin incrustarse, mientras que las partículas retenidas se limpian fácilmente mediante retrolavado o lavado por ultrasonidos.",
        "Fabricados en diámetros desde 10 mm hasta 600 mm y longitudes continuas de hasta 3.000 mm, incorporan bridas mecanizadas, roscas o anillos para su adaptación a recipientes a presión.",
      ],
      keyPoints: [
        "Perfil continuo en V que garantiza superficies suaves y sin rebabas con baja resistencia hidráulica.",
        "Uniones soldadas por resistencia en cada intersección entre hilo de superficie y varilla soporte.",
        "Fabricación en AISI 304 y 316L apta para esterilización con vapor, lavados químicos y retrolavado.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Cilindro de tubo filtrante de malla de alambre de acero inoxidable",
      },
    },
    specTable: {
      heading: "Parámetros Dimensionales y de Fabricación",
      intro: "Rangos estándar de dimensiones para tubos de criba cilíndricos. Se fabrican aberturas especiales y adaptaciones según plano del cliente.",
      columns: [
        { key: "parameter", label: "Parámetro", align: "left" },
        { key: "range", label: "Rango Estándar", align: "left" },
        { key: "tolerance", label: "Tolerancia", align: "left" },
        { key: "notes", label: "Notas Técnicas", align: "left" },
      ],
      rows: [
        {
          id: "slot-width",
          parameter: "Ancho de ranura",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Espaciado continuo a lo largo de toda la zona útil",
        },
        {
          id: "diameter",
          parameter: "Diámetro exterior (OD)",
          range: "10 – 600 mm",
          tolerance: "±0,5 mm",
          notes: "Dimensiones adaptadas para alojamientos compactos",
        },
        {
          id: "length",
          parameter: "Longitud total",
          range: "100 – 3.000 mm",
          tolerance: "±1,0 mm",
          notes: "Tramos mayores unidos mediante soldadura circunferencial",
        },
        {
          id: "materials",
          parameter: "Aleaciones",
          range: "AISI 304, AISI 316L",
          tolerance: "Composición estándar",
          notes: "Certificados de material disponibles bajo pedido",
        },
        {
          id: "end-connections",
          parameter: "Conexiones finales",
          range: "Bridas, Roscas NPT/BSPT, Anillos soldables",
          tolerance: "Mecanizado de precisión",
          notes: "Extremos lisos o casquillos torneados",
        },
      ],
      caption: "Datos confirmados de fabricación. Configuraciones adicionales según especificación.",
    },
    features: {
      heading: "Ventajas Constructivas y Estándares de Diseño",
      intro: "Ingeniería orientada a evitar la colmatación prematura y el desplazamiento de alambres en procesos continuos.",
      items: [
        {
          title: "Geometría en V Anticolmatación",
          text: "La abertura ensanchada hacia el interior permite que los sólidos se desprendan rápidamente durante el ciclo de retrolavado.",
        },
        {
          title: "Estructura Monolítica Soldada",
          text: "Cada cruce entre alambre exterior y varilla soporte longitudinal cuenta con soldadura por resistencia sólida.",
        },
        {
          title: "Opciones de Dirección de Flujo",
          text: "Disponible con flujo de fuera hacia adentro (FOTI) o de dentro hacia afuera (FITO) según la función requerida.",
        },
        {
          title: "Mecanizados de Extremo Precisos",
          text: "Casquillos, roscas y bridas maquinadas soldadas a ras para garantizar estanqueidad absoluta sin derivaciones.",
        },
      ],
    },
    related: {
      heading: "Configuraciones Tubulares Especializadas",
      intro: "Explore modelos adaptados para pozos profundos, drenajes de lecho filtrante y elementos filtrantes tipo bujía.",
      cards: [
        {
          title: "Tuberías Ranuradas y Colectores Laterales",
          text: "Alambre continuo de perfil en V soldado por fusión a varillas de soporte longitudinales para pozos de captación y distribución uniforme.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Tubo de criba de alambre de cuña de acero inoxidable con ranura continua",
          },
          badge: "Pozos y Drenaje",
          linkLabel: "Ver Tuberías Ranuradas",
        },
        {
          title: "Tubos de Alambre Cuña y Elementos Tipo Bujía",
          text: "Tubos de ranura continua y elementos tipo bujía totalmente soldados para separación a alta presión y recuperación de catalizadores.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Elemento filtrante de tubo de alambre cuña de acero inoxidable",
          },
          badge: "Recipientes a Presión",
          linkLabel: "Ver Elementos Tipo Bujía",
        },
        {
          title: "Gama Completa de Alambre en Cuña",
          text: "Visión integral de paneles planos, cribas curvadas, cilindros y toberas en alambre de cuña de acero inoxidable.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Resumen de productos de alambre en cuña",
          },
          badge: "Categoría Principal",
          linkLabel: "Volver a Alambre en Cuña",
        },
      ],
    },
    cta: {
      id: "screen-tubes-quote",
      eyebrow: "Fabricación de Tubos a Medida",
      heading: "Solicite Cotización Técnica para Tubos Filtrantes",
      lead: "Indique la luz de ranura requerida, diámetro, longitud, sentido de paso y tipo de conexión. Nuestro departamento técnico elaborará la propuesta correspondiente.",
      secondaryLabel: "Ver Productos de Alambre en Cuña",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Informes de prueba de materiales (MTR) y verificación PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "ScreenTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Spaltsiebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Präzisions-Filterrohre & Spaltrohre aus Edelstahl",
      lead: "Präzisionsgefertigte zylindrische Spaltsiebe und vollverschweißte Spaltrohrbaugruppen für extreme Differenzdrücke, hohe Kollapsfestigkeit und zuverlässige Partikelabscheidung.",
      secondaryCta: {
        label: "Spaltsiebe Übersicht",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Edelstahl-Drahtgewebe-Filterrohrzylinder",
      },
      cardNote: "Vollverschweißte Spaltrohre",
      cardLink: {
        label: "Technische Daten",
        to: "/ScreenTubesPage#specifications",
      },
      specs: [
        { label: "Spaltweitenbereich", value: "0,02 – 5,0 mm" },
        { label: "Außendurchmesser", value: "10 – 600 mm" },
        { label: "Standard-Werkstoffe", value: "AISI 304 / 316L" },
        { label: "Elementlänge", value: "Bis zu 3.000 mm" },
      ],
    },
    overview: {
      eyebrow: "Strukturstabilität & Strömungseffizienz",
      heading: "Kontinuierliche V-Draht-Schlitze für anspruchsvolle Rohrfiltration",
      paragraphs: [
        "Widerstandsgeschweißte Spaltrohre aus Edelstahl bieten höchste mechanische Steifigkeit, exakte Schlitzgeometrie und zuverlässige Stabilität gegen Kollaps bei hohen Druckdifferenzen.",
        "Die sich nach innen erweiternde V-Draht-Form erzeugt an der Außenfläche eine punktuelle Zweipunktauflage. Feststoffe passieren die Spalte ohne sich festzuklemmen, während zurückgehaltene Partikel im Rückspülprozess leicht abgelöst werden.",
        "Mit Außendurchmessern von 10 mm bis 600 mm und Längen bis zu 3.000 mm werden die Rohre mit passgenauen Flanschen, Gewinden oder Innenstützkörben für Druckbehälter konfektioniert.",
      ],
      keyPoints: [
        "Kontinuierliche Schlitzführung bietet glatte Oberflächen bei minimalem hydraulischem Strömungswiderstand.",
        "Widerstandsgeschweißte Kreuzungspunkte zwischen Profildrähten und Längstragstäben gewährleisten hohe Druckstoßfestigkeit.",
        "Robuste Fertigung in AISI 304 und 316L für wiederholte chemische Reinigung, Dampfsterilisation und Rückspülung.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Edelstahl-Drahtgewebe-Filterrohrzylinder",
      },
    },
    specTable: {
      heading: "Abmessungen & Fertigungsparameter",
      intro: "Standardabmessungen zylindrischer Spaltrohre. Kundenspezifische Spaltweiten und Sonderanschlüsse werden nach Zeichnung gefertigt.",
      columns: [
        { key: "parameter", label: "Parameter", align: "left" },
        { key: "range", label: "Standardbereich", align: "left" },
        { key: "tolerance", label: "Fertigungstoleranz", align: "left" },
        { key: "notes", label: "Hinweise", align: "left" },
      ],
      rows: [
        {
          id: "slot-width",
          parameter: "Spaltweite",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Gleichmäßige Schlitzweite über die gesamte Nutzlänge",
        },
        {
          id: "diameter",
          parameter: "Außendurchmesser (OD)",
          range: "10 – 600 mm",
          tolerance: "±0,5 mm",
          notes: "Präzise Dornmaße für kompakte Gehäusepassungen",
        },
        {
          id: "length",
          parameter: "Gesamtlänge",
          range: "100 – 3.000 mm",
          tolerance: "±1,0 mm",
          notes: "Verlängerung durch präzise Rundnahtverschweißung möglich",
        },
        {
          id: "materials",
          parameter: "Edelstahllegierungen",
          range: "AISI 304, AISI 316L",
          tolerance: "Standardanalyse",
          notes: "Werksprüfzeugnisse bei Auftragsvergabe erhältlich",
        },
        {
          id: "end-connections",
          parameter: "Endanschlüsse",
          range: "Flansche, Gewinde NPT/BSPT, Schweißenden",
          tolerance: "Präzisionsdrehteile",
          notes: "Glatt geschnitten oder mit gedrehten Anschlussmuffen",
        },
      ],
      caption: "Bestätigte Fertigungsbereiche. Individuelle Auslegungen auf Anfrage.",
    },
    features: {
      heading: "Konstruktionsvorteile & Qualitätsstandards",
      intro: "Ausgelegt zur Vermeidung von Verstopfung, Maschenverschiebung und Materialermüdung bei kontinuierlicher Trennung.",
      items: [
        {
          title: "Verstopfungsfreies V-Drahtprofil",
          text: "Der sich nach innen erweiternde Spalt sorgt für zuverlässiges Ablösen von Partikeln beim Rückspülen.",
        },
        {
          title: "Vollverschweißte Käfigstruktur",
          text: "Jeder Kreuzungspunkt zwischen Draht und Tragstab ist widerstandsverschweißt für maximale Torsions- und Druckstabilität.",
        },
        {
          title: "Flexible Strömungsrichtung",
          text: "Ausführungen für Durchströmung von außen nach innen (FOTI) oder von innen nach außen (FITO).",
        },
        {
          title: "Präzise Endbearbeitung",
          text: "Gedrehte Anschlussringe, Gewindenippel und Flansche verhindern zuverlässig Bypass-Leckagen.",
        },
      ],
    },
    related: {
      heading: "Spezifische Siebrohr-Konfigurationen",
      intro: "Entdecken Sie optimierte Ausführungen für Brunnenbau, Medienverteilung und Druckbehälter-Filterkerzen.",
      cards: [
        {
          title: "Filterrohre & Sternverteilersysteme",
          text: "Kontinuierlicher V-Profildraht auf Längstragstäben für Tiefbrunnen, Harzfallen und gleichmäßige Medienverteilung.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Spaltrohr aus Edelstahl mit kontinuierlicher Spaltweite",
          },
          badge: "Brunnen & Verteilung",
          linkLabel: "Filterrohre ansehen",
        },
        {
          title: "Spaltrohre & Spaltfilterelemente",
          text: "Vollverschweißte Endlosspalt-Spaltrohre und Filterkerzen für Hochdrucktrennung, Katalysatorrückgewinnung und anspruchsvolle Filtration.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Industrielles Spaltrohr-Filterelement aus Edelstahl",
          },
          badge: "Druckbehälter",
          linkLabel: "Spaltfilterkerzen ansehen",
        },
        {
          title: "Spaltsiebe Produktübersicht",
          text: "Vollständiges Programm an Spaltsiebböden, Bogensieben, Zylindern und Düsen aus Edelstahl.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Übersicht Spaltsiebprodukte",
          },
          badge: "Hauptkategorie",
          linkLabel: "Zurück zu Spaltsiebe",
        },
      ],
    },
    cta: {
      id: "screen-tubes-quote",
      eyebrow: "Individuelle Spaltrohrfertigung",
      heading: "Technisches Angebot für Filterrohre anfordern",
      lead: "Nennen Sie uns Spaltweite, Durchmesser, Einbaulänge, Strömungsrichtung und Anschlussart. Unser Engineering erarbeitet das passende Angebot.",
      secondaryLabel: "Spaltsiebe Entdecken",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen bei entsprechender Bestellangabe verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "ScreenTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Fil en Coin",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubes Filtrants de Précision en Acier Inoxydable",
      lead: "Cylindres filtrants et assemblages tubulaires en fil profilé mécano-soudé, conçus pour supporter des pressions différentielles sévères et assurer une rétention précise des solides.",
      secondaryCta: {
        label: "Gamme Fil en Coin",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Tube filtrant cylindrique en toile métallique d'acier inoxydable",
      },
      cardNote: "Tubes Soudés Haute Rigidité",
      cardLink: {
        label: "Spécifications",
        to: "/ScreenTubesPage#specifications",
      },
      specs: [
        { label: "Ouverture de fente", value: "0,02 – 5,0 mm" },
        { label: "Diamètre extérieur", value: "10 – 600 mm" },
        { label: "Nuances d'alliage", value: "AISI 304 / 316L" },
        { label: "Longueur d'élément", value: "Jusqu'à 3 000 mm" },
      ],
    },
    overview: {
      eyebrow: "Rigidité Mécanique & Capacité Hydraulique",
      heading: "Fente Continue en V pour Filtrations Tubulaires Exigeantes",
      paragraphs: [
        "Les tubes filtrants en fil profilé soudés par résistance procurent une excellente résistance à l'écrasement, des ouvertures régulières et une parfaite tenue face aux pics de pression.",
        "Le profil en V s'élargissant vers l'intérieur garantit un contact ponctuel en deux points. Les fines particules traversent sans coincement, tandis que le gâteau accumulé à l'extérieur s'élimine aisément par rétro-lavage ou nettoyage ultrasons.",
        "Disponibles de 10 mm à 600 mm de diamètre et jusqu'à 3 000 mm de longueur continue, ils sont équipés de brides, embouts filetés ou bagues d'extrémité usinées.",
      ],
      keyPoints: [
        "Profilé continu en V réduisant les frottements et offrant une grande surface ouverte efficace.",
        "Soudure par résistance à chaque intersection garantissant une haute tenue à l'éclatement et à la dépression.",
        "Construction en inox AISI 304 et 316L adaptée aux lavages chimiques, à la vapeur et aux rétro-lavages.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Tube filtrant cylindrique en toile métallique d'acier inoxydable",
      },
    },
    specTable: {
      heading: "Paramètres Dimensionnels et de Fabrication",
      intro: "Plages dimensionnelles courantes des tubes en fil profilé. Fentes et embouts spéciaux réalisés selon vos plans.",
      columns: [
        { key: "parameter", label: "Paramètre", align: "left" },
        { key: "range", label: "Plage Standard", align: "left" },
        { key: "tolerance", label: "Tolérance", align: "left" },
        { key: "notes", label: "Remarques", align: "left" },
      ],
      rows: [
        {
          id: "slot-width",
          parameter: "Ouverture de fente",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Espacement rigoureusement régulier sur toute la longueur",
        },
        {
          id: "diameter",
          parameter: "Diamètre extérieur (OD)",
          range: "10 – 600 mm",
          tolerance: "±0,5 mm",
          notes: "Mandrins d'ajustement pour corps de filtre étroits",
        },
        {
          id: "length",
          parameter: "Longueur hors-tout",
          range: "100 – 3 000 mm",
          tolerance: "±1,0 mm",
          notes: "Assemblage de longueurs supérieures par soudure circulaire",
        },
        {
          id: "materials",
          parameter: "Nuances d'acier",
          range: "AISI 304, AISI 316L",
          tolerance: "Composition conforme",
          notes: "Certificats matière disponibles sur demande à la commande",
        },
        {
          id: "end-connections",
          parameter: "Raccordements d'extrémité",
          range: "Brides, Raccords filetés NPT/BSPT, Bagues à souder",
          tolerance: "Usinage standard",
          notes: "Extrémités lisses ou embouts tournés dans la masse",
        },
      ],
      caption: "Données de fabrication certifiées. Autres variantes disponibles sur cahier des charges.",
    },
    features: {
      heading: "Avantages Structurels et Finitions",
      intro: "Conception fiabilisée évitant le colmatage irréversible et la déformation sous charge continue.",
      items: [
        {
          title: "Profil en V Autonettoyant",
          text: "L'élargissement intérieur de la fente permet une libération instantanée des particules lors des impulsions de décolmatage.",
        },
        {
          title: "Assemblage Entièrement Soudé",
          text: "Toutes les liaisons entre le fil extérieur et les tiges de renfort axiales sont fusionnées par électrosoudure.",
        },
        {
          title: "Sens de Passage Adaptable",
          text: "Fabrication pour flux de l'extérieur vers l'intérieur (FOTI) ou de l'intérieur vers l'extérieur (FITO).",
        },
        {
          title: "Usinages d'Extrémités Précis",
          text: "Embouts massifs tournés, brides étanches et pas de vis raccordés sans discontinuité avec la surface filtrante.",
        },
      ],
    },
    related: {
      heading: "Configurations Tubulaires Spécialisées",
      intro: "Découvrez les variantes dédiées aux forages, crépines de fond de cuve et bougies filtrantes sous pression.",
      cards: [
        {
          title: "Tubes Crépines et Collecteurs Latéraux",
          text: "Fil continu à profil en V soudé par fusion sur des tiges longitudinales pour puits d'eau et distribution uniforme.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Tube crépine en acier inoxydable à fente continue",
          },
          badge: "Forage & Collecteurs",
          linkLabel: "Voir les Tubes Crépines",
        },
        {
          title: "Tubes à Fil en Coin et Bougies Filtrantes",
          text: "Tubes à fente continue et éléments bougies soudés conçus pour la séparation haute pression et la récupération de catalyseur.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Élément filtrant en fil en coin en acier inoxydable",
          },
          badge: "Cuves sous Pression",
          linkLabel: "Voir les Bougies Filtrantes",
        },
        {
          title: "Gamme Complète Fil en Coin",
          text: "Panneaux plans, grilles courbes, cylindres et crépines en fil en coin inox pour l'industrie de transformation.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Vue d'ensemble des produits en fil en coin",
          },
          badge: "Catégorie Mère",
          linkLabel: "Retour au Fil en Coin",
        },
      ],
    },
    cta: {
      id: "screen-tubes-quote",
      eyebrow: "Fabrication de Tubes sur Mesure",
      heading: "Demandez une Étude Technique pour Tubes Filtrants",
      lead: "Précisez l'ouverture de fente, le diamètre extérieur, la longueur utile et les embouts d'adaptation souhaités pour recevoir notre offre chiffrée.",
      secondaryLabel: "Consulter la Gamme Fil en Coin",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Certificats matière (MTR) et contrôles spectrométriques PMI disponibles sur demande formulée à la commande.",
    },
  },
  pt: {
    breadcrumbKey: "ScreenTubes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Arame em Cunha",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubos de Filtragem de Precisão em Aço Inoxidável",
      lead: "Cilindros e conjuntos tubulares de arame em cunha totalmente soldados, desenvolvidos para suportar alta pressão diferencial, esforço de colapso e retenção precisa de sólidos industriais.",
      secondaryCta: {
        label: "Visão Geral de Arame em Cunha",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Cilindro de tubo filtrante de malha de aço inoxidável",
      },
      cardNote: "Tubos de Ranhura Contínua",
      cardLink: {
        label: "Especificações",
        to: "/ScreenTubesPage#specifications",
      },
      specs: [
        { label: "Abertura de ranhura", value: "0,02 – 5,0 mm" },
        { label: "Diâmetro externo", value: "10 – 600 mm" },
        { label: "Ligas padrão", value: "AISI 304 / 316L" },
        { label: "Comprimento", value: "Até 3.000 mm" },
      ],
    },
    overview: {
      eyebrow: "Rigidez Mecânica & Eficiência de Vazão",
      heading: "Ranhura Contínua em V para Filtragem Tubular Rigorosa",
      paragraphs: [
        "Os tubos filtrantes de tela tipo cunha eletrosoldados oferecem excelente rigidez estrutural, precisão dimensional de ranhura e elevada resistência contra esforços de colapso.",
        "O perfil em V com alargamento interno assegura contato linear de apenas dois pontos com as partículas retidas. Os finos atravessam livremente o canal de alívio sem incrustar, enquanto os sólidos retidos são expelidos facilmente por retrolavagem.",
        "Fabricados com diâmetros de 10 mm a 600 mm e comprimentos contínuos até 3.000 mm, incorporam conexões flangeadas, roscadas ou anéis usinados para montagem perfeita em vasos industriais.",
      ],
      keyPoints: [
        "Perfil contínuo em V que mantém superfícies lisas com baixa perda de carga hidráulica.",
        "Solda por fusão em cada cruzamento entre arame perfilado e haste suporte para conter surtos de pressão.",
        "Fabricação sólida em AISI 304 e 316L para suportar lavagens ácidas, esterilização e retrolavagens periódicas.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
        alt: "Cilindro de tubo filtrante de malha de aço inoxidável",
      },
    },
    specTable: {
      heading: "Parâmetros Dimensionais e de Fabricação",
      intro: "Dimensões padrão dos tubos de arame em cunha. Ranhuras especiais e terminações são fabricadas conforme desenho técnico.",
      columns: [
        { key: "parameter", label: "Parâmetro", align: "left" },
        { key: "range", label: "Faixa Padrão", align: "left" },
        { key: "tolerance", label: "Tolerância", align: "left" },
        { key: "notes", label: "Observações", align: "left" },
      ],
      rows: [
        {
          id: "slot-width",
          parameter: "Abertura de ranhura",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Espaçamento rigorosamente homogêneo ao longo do tubo",
        },
        {
          id: "diameter",
          parameter: "Diâmetro externo (OD)",
          range: "10 – 600 mm",
          tolerance: "±0,5 mm",
          notes: "Mandris calibrados para encaixe em carcaças compactas",
        },
        {
          id: "length",
          parameter: "Comprimento total",
          range: "100 – 3.000 mm",
          tolerance: "±1,0 mm",
          notes: "União de extensões maiores por soldagem circunferencial",
        },
        {
          id: "materials",
          parameter: "Ligas de aço inox",
          range: "AISI 304, AISI 316L",
          tolerance: "Análise padrão",
          notes: "Certificados de ensaio de material disponíveis no pedido",
        },
        {
          id: "end-connections",
          parameter: "Terminações e conexões",
          range: "Flanges, Roscas NPT/BSPT, Anéis de solda",
          tolerance: "Usinagem precisa",
          notes: "Extremos lisos ou adaptadores torneados de topo",
        },
      ],
      caption: "Faixas dimensionais confirmadas. Projetos especiais fornecidos sob especificação.",
    },
    features: {
      heading: "Diferenciais Construtivos e Confiabilidade",
      intro: "Projetados para eliminar o risco de entupimento cego e deformação mecânica sob operação ininterrupta.",
      items: [
        {
          title: "Geometria em V Autolimpante",
          text: "Abertura que se alarga internamente permitindo desprendimento ágil da torta de filtração durante o ciclo reverso.",
        },
        {
          title: "Gaiola Rígida Eletrosoldada",
          text: "Cada interseção entre perfil superficial e barra axial de apoio é fundida por resistência em peça única.",
        },
        {
          title: "Sentido de Fluxo Customizado",
          text: "Disponível para escoamento de fora para dentro (FOTI) ou de dentro para fora (FITO).",
        },
        {
          title: "Terminações Mecânicas Perfeitas",
          text: "Flanges usinadas, roscas padronizadas e anéis maciços soldados de forma estanque para eliminar bypass.",
        },
      ],
    },
    related: {
      heading: "Configurações Tubulares Especializadas",
      intro: "Conheça versões dedicadas a poços profundos, drenos de fundo de leito e elementos vela para vasos pressurizados.",
      cards: [
        {
          title: "Tubos de Peneira e Coletores Laterais",
          text: "Arame contínuo de perfil em V soldado a hastes longitudinais para poços profundos, drenagem e distribuição uniforme de fluidos.",
          to: "/ScreenPipesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
            alt: "Tubo de peneira de arame em cunha de aço inoxidável com ranhura contínua",
          },
          badge: "Poços e Drenos",
          linkLabel: "Ver Tubos de Peneira",
        },
        {
          title: "Tubos Tipo Cunha e Elementos Vela",
          text: "Tubos de tela perfilada e elementos vela totalmente soldados para separação sob alta pressão e retenção de catalisadores.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Elemento filtrante de tubo de tela tipo cunha em aço inoxidável",
          },
          badge: "Vasos Pressurizados",
          linkLabel: "Ver Elementos Vela",
        },
        {
          title: "Linha Completa de Telas em Cunha",
          text: "Visão abrangente de painéis planos, telas curvas, cilindros e bocais industriais de arame em cunha.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Visão geral da linha de arame em cunha",
          },
          badge: "Categoria Principal",
          linkLabel: "Voltar para Arame em Cunha",
        },
      ],
    },
    cta: {
      id: "screen-tubes-quote",
      eyebrow: "Fabricação Sob Medida de Tubos",
      heading: "Solicite uma Cotação Técnica para Tubos Filtrantes",
      lead: "Informe a abertura de ranhura desejada, diâmetro externo, comprimento útil, direção do fluxo e terminações necessárias para receber nossa proposta técnica.",
      secondaryLabel: "Explorar Telas de Arame em Cunha",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Relatórios de teste de material (MTR) e ensaio PMI disponíveis quando solicitados no pedido.",
    },
  },
};
